/**
 * Authoritative in-progress mock sitting. Survives hard reload.
 * Prefer localStorage (tab-independent) with sessionStorage as a migration fallback.
 *
 * Keys:
 * - Account: cfa_active_sit_{level}_{uid|guest}_{topicSlug}_{formId}
 * - Route mirror (uid-agnostic, same browser): cfa_active_sit_route_{level}_{topicSlug}_{formId}
 *   Written alongside the account key so reload can resume even if auth uid was not
 *   ready on the previous write, or guest↔uid mismatched between save and restore.
 */

export function activeSittingKey({ level = 'l1', uid, topicSlug, formId }) {
  return `cfa_active_sit_${level || 'l1'}_${uid || 'guest'}_${topicSlug}_${formId || 1}`;
}

/** Browser-local mirror that does not depend on auth uid. */
export function routeSittingKey({ level = 'l1', topicSlug, formId }) {
  return `cfa_active_sit_route_${level || 'l1'}_${topicSlug}_${formId || 1}`;
}

export function newAttemptId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `sit_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function storageGet(key) {
  if (typeof window === 'undefined') return null;
  try {
    const local = window.localStorage.getItem(key);
    if (local) return local;
  } catch {
    /* private mode */
  }
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key, value) {
  if (typeof window === 'undefined') return false;
  let ok = false;
  try {
    window.localStorage.setItem(key, value);
    ok = true;
  } catch {
    /* quota / private */
  }
  try {
    window.sessionStorage.setItem(key, value);
    ok = true;
  } catch {
    /* ignore */
  }
  return ok;
}

function storageRemove(key) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** Drop bulky bank caches so a sitting write can succeed under quota pressure. */
function evictQuestionBankCaches() {
  if (typeof window === 'undefined') return;
  ['cfa_questions_bank_v5', 'cfa_questions_bank_v4'].forEach((key) => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  });
}

function parseSitting(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Away this long, with time still on the clock, counts as a break. */
export const BREAK_GAP_MS = 2 * 60 * 1000;
/** grade-sitting tokens expire 48h after issue (TOKEN_TTL_MS in netlify/functions/_quizServer.js). */
export const SITTING_TOKEN_TTL_MS = 48 * 60 * 60 * 1000;
/** Finish must reach the server this long before the token expires. */
export const TOKEN_EXPIRY_MARGIN_MS = 10 * 60 * 1000;

/** Issue time (ms) inside a grade-sitting token, 0 when unreadable. Not verified: timing only. */
export function sittingTokenIssuedAt(token) {
  try {
    const body = String(token || '').split('.')[0].replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(atob(body + '='.repeat((4 - (body.length % 4)) % 4)));
    return Number.isFinite(json?.iat) ? json.iat : 0;
  } catch {
    return 0;
  }
}

/** Latest moment a sitting can still be graded (token expiry less a margin), 0 when unknown. */
function gradeDeadlineMs(saved) {
  const startMs = Number(saved.quizStartTimeMs || saved.quizStartTime) || 0;
  const issued = sittingTokenIssuedAt(saved.sittingToken) || Number(saved.issuedAtMs) || startMs;
  return issued ? issued + SITTING_TOKEN_TTL_MS - TOKEN_EXPIRY_MARGIN_MS : 0;
}

/**
 * Closing or leaving the exam pauses its clock: the exam stamps `leftAtMs` when the page is
 * hidden, closed, or navigated away from (any later activity clears it). Reopened after a
 * break, the sitting's start and deadline shift forward by the time away, so the student keeps
 * the time they had left, never past the grade token's expiry. Sittings left after their
 * deadline, or reopened within BREAK_GAP_MS, are unchanged.
 */
export function applyBreakCredit(saved, now = Date.now()) {
  if (!saved) return saved;
  const leftAt = Number(saved.leftAtMs) || 0;
  const endsAt = Number(saved.endsAtMs) || 0;
  const startMs = Number(saved.quizStartTimeMs || saved.quizStartTime) || 0;
  if (!leftAt || !endsAt || !startMs || leftAt >= endsAt) return saved;
  const gap = now - leftAt;
  if (gap <= BREAK_GAP_MS) return saved;
  const deadline = gradeDeadlineMs(saved);
  const shiftedEnd = deadline ? Math.min(endsAt + gap, deadline) : endsAt + gap;
  return {
    ...saved,
    issuedAtMs: Number(saved.issuedAtMs) || startMs,
    quizStartTimeMs: startMs + (shiftedEnd - endsAt),
    endsAtMs: shiftedEnd,
    leftAtMs: 0,
  };
}

/** True when payload can resume an exam without re-sampling the bank. */
export function isResumableSitting(saved, { level, now = Date.now() } = {}) {
  if (!saved || !Array.isArray(saved.questions) || !saved.questions.length) return false;
  if (saved.level && level && saved.level !== level) return false;
  const startMs = Number(saved.quizStartTimeMs || saved.quizStartTime) || 0;
  if (!startMs) return false;
  // Past the grade token's expiry the server refuses Finish, so there is nothing to resume.
  const deadline = gradeDeadlineMs(saved);
  if (deadline && now > deadline - BREAK_GAP_MS) return false;
  const endsAtMs = Number(applyBreakCredit(saved, now).endsAtMs) || 0;
  // Soft expiry: keep resume for a short grace after timer end so finish can still land.
  if (endsAtMs && now > endsAtMs + 30 * 60 * 1000) return false;
  return true;
}

/**
 * Candidate storage keys for a route, including legacy (pre-level) and guest/uid swaps.
 */
export function sittingKeyCandidates({ level = 'l1', uid, topicSlug, formId }) {
  const form = formId || 1;
  const lv = level || 'l1';
  const owner = uid || 'guest';
  const keys = [
    activeSittingKey({ level: lv, uid: owner, topicSlug, formId: form }),
    routeSittingKey({ level: lv, topicSlug, formId: form }),
    // Legacy unscoped key from early PHI2-109 builds (no level segment).
    `cfa_active_sit_${owner}_${topicSlug}_${form}`,
  ];
  if (owner !== 'guest') {
    keys.push(activeSittingKey({ level: lv, uid: 'guest', topicSlug, formId: form }));
    keys.push(`cfa_active_sit_guest_${topicSlug}_${form}`);
  }
  return [...new Set(keys.filter(Boolean))];
}

function scanStorageForRouteSitting({ level, topicSlug, formId, now }) {
  if (typeof window === 'undefined') return null;
  const form = String(formId || 1);
  const lv = level || 'l1';
  const needle = `_${topicSlug}_${form}`;
  const stores = [window.localStorage, window.sessionStorage];
  for (const store of stores) {
    let i = 0;
    try {
      for (; i < store.length; i += 1) {
        const key = store.key(i);
        if (!key || !key.startsWith('cfa_active_sit')) continue;
        if (!key.endsWith(needle)) continue;
        // Prefer level-scoped / route keys; skip obvious other-level account keys.
        if (key.includes('_sit_') && !key.includes(`_sit_${lv}_`) && !key.includes('_sit_route_') && !key.includes(`_sit_guest_`)) {
          // Legacy keys have no level — still consider them.
          if (key.startsWith('cfa_active_sit_l1_') || key.startsWith('cfa_active_sit_l2_') || key.startsWith('cfa_active_sit_l3_')) {
            continue;
          }
        }
        const saved = parseSitting(store.getItem(key));
        if (isResumableSitting(saved, { level: lv, now })) {
          return { key, saved };
        }
      }
    } catch {
      /* private mode / SecurityError */
    }
  }
  return null;
}

/**
 * Find a resumable sitting for this route across account, guest, route-mirror,
 * and legacy keys. Migrates a hit onto the canonical account + route keys.
 */
export function findResumableSitting(keyParts, opts = {}) {
  const now = opts.now || Date.now();
  const level = keyParts.level || opts.level || 'l1';
  const candidates = sittingKeyCandidates({ ...keyParts, level });
  for (const key of candidates) {
    const raw = storageGet(key);
    if (!raw) continue;
    const saved = parseSitting(raw);
    if (!isResumableSitting(saved, { level, now }) || !sittingBankLooksCompatible(saved, level)) {
      storageRemove(key);
      continue;
    }
    // Promote to canonical keys when found under a legacy/guest/route key.
    const credited = applyBreakCredit(saved, now);
    writeActiveSitting(keyParts, credited, { keepUpdatedAt: true });
    return credited;
  }
  const scanned = scanStorageForRouteSitting({
    level,
    topicSlug: keyParts.topicSlug,
    formId: keyParts.formId,
    now,
  });
  if (scanned?.saved) {
    const credited = applyBreakCredit(scanned.saved, now);
    writeActiveSitting(keyParts, credited, { keepUpdatedAt: true });
    return credited;
  }
  return null;
}

export function readActiveSitting(keyParts, opts = {}) {
  if (typeof keyParts === 'string') {
    const raw = storageGet(keyParts);
    if (!raw) return null;
    try {
      const saved = JSON.parse(raw);
      if (!isResumableSitting(saved, opts)) {
        storageRemove(keyParts);
        return null;
      }
      return applyBreakCredit(saved, opts.now || Date.now());
    } catch {
      storageRemove(keyParts);
      return null;
    }
  }
  return findResumableSitting(keyParts, opts);
}

/**
 * Normalized sitting record (what localStorage and the cross-device mirror hold).
 * `updatedAtMs` is the last time the student worked on it: a write stamps now, unless
 * `keepUpdatedAt` copies a sitting found elsewhere (legacy key, another device).
 */
export function buildSittingBody(payload, { keepUpdatedAt = false } = {}) {
  const startMs = Number(payload.quizStartTimeMs || payload.quizStartTime) || Date.now();
  const durationSeconds = Number(payload.durationSeconds) || 0;
  return {
    attemptId: payload.attemptId || newAttemptId(),
    level: payload.level || 'l1',
    questions: payload.questions,
    currentQuestion: payload.currentQuestion || 0,
    selectedAnswers: payload.selectedAnswers || [],
    flagged: payload.flagged || [],
    struck: payload.struck || {},
    highlights: payload.highlights || {},
    questionSeconds: payload.questionSeconds || [],
    quizStartTimeMs: startMs,
    // First issue time; quizStartTimeMs moves forward with break credit, this does not.
    issuedAtMs: Number(payload.issuedAtMs) || startMs,
    endsAtMs:
      Number(payload.endsAtMs) ||
      (durationSeconds > 0 ? startMs + durationSeconds * 1000 : 0),
    durationSeconds,
    // Signed grade-sitting token for these items (server marking at Finish).
    sittingToken: typeof payload.sittingToken === 'string' ? payload.sittingToken : '',
    // When the student left the exam page (0 while they are on it); see applyBreakCredit.
    leftAtMs: Number(payload.leftAtMs) || 0,
    started: true,
    updatedAtMs: (keepUpdatedAt && Number(payload.updatedAtMs)) || Date.now(),
  };
}

export function writeActiveSitting(keyParts, payload, { keepUpdatedAt = false } = {}) {
  const parts =
    typeof keyParts === 'string'
      ? null
      : {
          level: keyParts.level || payload.level || 'l1',
          uid: keyParts.uid,
          topicSlug: keyParts.topicSlug,
          formId: keyParts.formId,
        };
  const accountKey = typeof keyParts === 'string' ? keyParts : activeSittingKey(parts);
  const body = buildSittingBody(payload, { keepUpdatedAt });
  const serialized = JSON.stringify(body);
  let ok = storageSet(accountKey, serialized);
  if (!ok) {
    evictQuestionBankCaches();
    ok = storageSet(accountKey, serialized);
  }
  if (parts) {
    const routeKey = routeSittingKey(parts);
    if (!storageSet(routeKey, serialized)) {
      evictQuestionBankCaches();
      storageSet(routeKey, serialized);
    }
  }
  return ok;
}

export function clearActiveSitting(keyParts) {
  if (typeof keyParts === 'string') {
    storageRemove(keyParts);
    return;
  }
  sittingKeyCandidates(keyParts).forEach((key) => storageRemove(key));
  storageRemove(routeSittingKey(keyParts));
}

/** Remaining section seconds from persisted deadlines (monotonic across reload). */
export function remainingSecondsFromSitting(saved, { durationSeconds, now = Date.now() } = {}) {
  const duration = Number(durationSeconds || saved?.durationSeconds) || 0;
  if (!duration) return 0;
  const endsAt =
    Number(saved?.endsAtMs) ||
    (Number(saved?.quizStartTimeMs || saved?.quizStartTime) || 0) + duration * 1000;
  if (!endsAt) return duration;
  return Math.max(0, Math.ceil((endsAt - now) / 1000));
}

/** Normalize bank labels like "CFA Level 1", "level 2", "l3", "1" → l1|l2|l3. */
export function normalizeBankLevel(raw) {
  const s = String(raw || '')
    .toLowerCase()
    .trim();
  if (!s) return '';
  if (s === 'l1' || s === 'l2' || s === 'l3') return s;
  if (s === '1' || s === '2' || s === '3') return `l${s}`;
  // Firestore items ship as "CFA Level 1" / "Level 2", etc.
  if (/level\s*3|\bl\s*3\b/.test(s)) return 'l3';
  if (/level\s*2|\bl\s*2\b/.test(s)) return 'l2';
  if (/level\s*1|\bl\s*1\b/.test(s)) return 'l1';
  return s;
}

export function sittingBankLooksCompatible(saved, level) {
  if (!saved?.questions?.length) return false;
  if (saved.level && level && saved.level !== level) return false;
  // CFA-23: refuse bloated sittings left by the practice-append race (~530 items).
  const lv = normalizeBankLevel(level || saved.level) || 'l1';
  if (lv === 'l1' && saved.questions.length > 90) return false;
  const first = saved.questions[0];
  // Firestore items use "CFA Level 1"; local bank often omits cfaLevel entirely.
  const bankLevel = normalizeBankLevel(first?.cfaLevel || first?.level);
  const want = normalizeBankLevel(level);
  if (bankLevel && want && bankLevel !== want) return false;
  const setId = String(first?.itemSetId || '');
  if (level === 'l2' && /^l3[-_]/i.test(setId)) return false;
  if (level === 'l3' && /^l2[-_]/i.test(setId)) return false;
  return true;
}

/**
 * Level 1 mock sittings in progress in this browser, newest first, for "Continue Mock N" prompts.
 * Reads the uid-agnostic route mirrors; `ownerUid` drops sittings signed for another account
 * (grade-sitting would refuse them). Read-only: never migrates or clears storage.
 */
export function findInProgressMocks({ ownerUid = '', tokenOwner = () => '', now = Date.now() } = {}) {
  if (typeof window === 'undefined') return [];
  const prefix = 'cfa_active_sit_route_l1_';
  const found = [];
  let store;
  try {
    store = window.localStorage;
  } catch {
    return [];
  }
  try {
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (!key || !key.startsWith(prefix)) continue;
      const match = /^session-(\d+)_(\d+)$/.exec(key.slice(prefix.length));
      if (!match) continue;
      const saved = parseSitting(store.getItem(key));
      if (!saved?.sittingToken || !isResumableSitting(saved, { level: 'l1', now })) continue;
      const owner = tokenOwner(saved.sittingToken);
      if (owner && owner !== ownerUid) continue;
      const credited = applyBreakCredit(saved, now);
      const total = saved.questions.length;
      const remainingSeconds = remainingSecondsFromSitting(credited, { now });
      if (remainingSeconds <= 0) continue;
      found.push({
        formId: Number(match[1]),
        path: `/quiz/session-${match[1]}`,
        currentQuestion: Math.min(total, (Number(saved.currentQuestion) || 0) + 1),
        answered: (saved.selectedAnswers || []).filter((a) => a !== null && a !== undefined).length,
        total,
        remainingSeconds,
        updatedAtMs: Number(saved.updatedAtMs) || 0,
      });
    }
  } catch {
    return [];
  }
  return found.sort((a, b) => b.updatedAtMs - a.updatedAtMs);
}
